<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  arrow: {
    type: Boolean,
    default: false,
    required: false,
  },
  primary: {
    type: Boolean,
    default: false,
    required: false,
  },
  variant: {
    type: String,
    default: 'default',
    required: false,
  },
  size: {
    type: String,
    default: 'md',
    required: false,
  },
  icon: {
    type: [Object, Function],
    default: null,
    required: false,
  },
  iconPosition: {
    type: String,
    default: 'left',
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false,
  },
})

const resolvedVariant = computed(() => {
  if (props.primary) return 'accent'
  if (
    ['default', 'accent', 'danger', 'ghost', 'pill'].includes(props.variant)
  ) {
    return props.variant
  }
  return 'default'
})

const resolvedSize = computed(() => {
  if (['md', 'sm'].includes(props.size)) return props.size
  return 'md'
})

const resolvedIconPosition = computed(() => {
  if (['left', 'right'].includes(props.iconPosition)) return props.iconPosition
  return 'left'
})
</script>

<template>
  <button
    :disabled="disabled"
    :class="[
      'button',
      `button--${resolvedVariant}`,
      `button--${resolvedSize}`,
      { 'button--disabled': disabled },
    ]">
    <span
      v-if="icon && resolvedIconPosition === 'left'"
      class="button-icon"
      aria-hidden="true">
      <component
        :is="icon"
        class="button-icon-svg" />
    </span>
    <span>{{ label }}</span>
    <span
      v-if="icon && resolvedIconPosition === 'right'"
      class="button-icon"
      aria-hidden="true">
      <component
        :is="icon"
        class="button-icon-svg" />
    </span>
  </button>
</template>

<style scoped>
.button {
  display: flex;
  border: none;
  padding: 0rem 3rem;
  height: 44px;
  min-width: 200px;
  color: var(--text-color);
  background-color: var(--background-color);
  border-radius: 25px;
  outline: 1px solid var(--background-reverse);
  font-family: var(--title-font);
  font-weight: var(--font-bold);
  cursor: pointer;
  transition-duration: 0.2s;
  align-self: center;
  justify-content: center;
  align-items: center;
  min-height: 44px;
  font-size: 0.9rem;
  gap: 0.5rem;
}

.button:disabled {
  cursor: not-allowed;
}

.button--disabled {
  opacity: 0.52;
  pointer-events: none;
}

.button--sm {
  min-width: 0;
  min-height: 0;
  height: auto;
  padding: 0.4rem 0.9rem;
}

.button-icon {
  display: inline-flex;
  width: 1.56rem;
  height: 1.56rem;
  flex-shrink: 0;
  line-height: 0;
}

.button-icon :deep(.button-icon-svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.button-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.button-light-mode {
  background-color: rgb(0, 0, 0);
}

.button:hover {
  transition-duration: 0.3s;
}

.button--accent {
  background-color: var(--primary-color);
  color: var(--white);
  outline: 1px solid var(--primary-color);
}

.button--danger {
  color: var(--warning-color);
  outline: 1px solid var(--warning-color);
  font-weight: 400;
}

.button--ghost {
  min-width: 0;
  min-height: 0;
  height: auto;
  padding: 0;
  border-radius: 0;
  outline: none;
  background: transparent;
  text-decoration: underline;
}

.button--ghost.button--danger {
  color: var(--warning-color);
}

.button--pill {
  min-width: 0;
  height: auto;
  min-height: 0;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: var(--background-color);
  outline: 1px solid var(--content-background);
  font-weight: 400;
  white-space: nowrap;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    color 120ms ease;
}

@media screen and (max-width: 768px) {
  .button {
    width: 100%;
  }
}
</style>
